/**
 * AktivitiTab - Daily activities tab (milk, diaper, sleep, etc.)
 */
import { useState } from "react";
import { X, Upload, Info, ZoomIn, ZoomOut } from "lucide-react";

const TIME_SLOTS_DAY = ['7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', 
                        '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM'];

const TIME_SLOTS_24H = [...TIME_SLOTS_DAY,
                        '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM', '11:00 PM', '12:00 AM',
                        '1:00 AM', '2:00 AM', '3:00 AM', '4:00 AM', '5:00 AM', '6:00 AM'];

export const STOOL_COLORS = [
  { value: 'Kuning Mustard', img: '/assets/stool/kuning_mustard.png' },
  { value: 'Coklat Mentega', img: '/assets/stool/coklat_mentega.png' },
  { value: 'Hijau', img: '/assets/stool/hijau.png' },
  { value: 'Kelabu Pucat/Putih', img: '/assets/stool/kelabu.png' },
  { value: 'Merah', img: '/assets/stool/merah.png' },
  { value: 'Hijau Kehitaman', img: '/assets/stool/hijau_kehitaman.png' }
];

export default function AktivitiTab({ formData, updateFormField, toggleTimeSelection, stoolPhotoFile, setStoolPhotoFile, use24hTimes = true }) {
  const [showInfographic, setShowInfographic] = useState(false);
  const [infographicZoom, setInfographicZoom] = useState(1);
  const TIME_SLOTS = use24hTimes ? TIME_SLOTS_24H : TIME_SLOTS_DAY;
  return (
    <div className="space-y-4 p-3 bg-pink-50 rounded-lg">
      {/* Susu */}
      <div>
        <label className="block text-sm font-medium text-pink-800 mb-2">Susu</label>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {TIME_SLOTS.map(time => (
              <button
                key={time}
                type="button"
                onClick={() => toggleTimeSelection('susu', time)}
                className={`px-2 py-1 text-xs rounded transition-all ${
                  formData.susu?.times?.includes(time) 
                    ? 'bg-pink-500 text-white' 
                    : 'bg-white border border-pink-200 hover:bg-pink-100'
                }`}
              >
                {time}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder="oz"
              value={formData.susu?.quantity_oz || ''}
              onChange={(e) => updateFormField('susu.quantity_oz', e.target.value)}
              className="w-20 px-3 py-1 border border-pink-200 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Nota..."
              value={formData.susu?.notes || ''}
              onChange={(e) => updateFormField('susu.notes', e.target.value)}
              className="flex-1 px-3 py-1 border border-pink-200 rounded-lg text-sm"
            />
          </div>
        </div>
      </div>

      {/* Lampin/Tandas */}
      <div>
        <label className="block text-sm font-medium text-yellow-800 mb-2">Lampin/Tandas</label>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {TIME_SLOTS.map(time => (
              <button
                key={time}
                type="button"
                onClick={() => toggleTimeSelection('diaper_change', time)}
                className={`px-2 py-1 text-xs rounded transition-all ${
                  formData.diaper_change?.times?.includes(time) 
                    ? 'bg-yellow-500 text-white' 
                    : 'bg-white border border-yellow-200 hover:bg-yellow-100'
                }`}
              >
                {time}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <select
              data-testid="stool-type-select"
              value={formData.diaper_change?.stool_type || ''}
              onChange={(e) => updateFormField('diaper_change.stool_type', e.target.value)}
              className="px-3 py-1 border border-yellow-200 rounded-lg text-sm"
            >
              <option value="">Jenis Najis *</option>
              <option value="normal">Normal</option>
              <option value="cair">Cair</option>
              <option value="keras">Keras</option>
            </select>
            <button
              type="button"
              data-testid="stool-infographic-button"
              onClick={() => { setInfographicZoom(1); setShowInfographic(true); }}
              className="flex items-center gap-1 px-2 py-1 bg-orange-100 hover:bg-orange-200 border border-orange-300 rounded-lg text-xs font-medium text-orange-700 transition-colors"
              title="Lihat infografik warna & maksud najis bayi"
            >
              <Info className="w-4 h-4" />
              Infografik
            </button>
            <input
              data-testid="stool-notes-input"
              type="text"
              placeholder="Nota... (wajib jika masa dipilih)"
              value={formData.diaper_change?.notes || ''}
              onChange={(e) => updateFormField('diaper_change.notes', e.target.value)}
              className="flex-1 px-3 py-1 border border-yellow-200 rounded-lg text-sm"
            />
          </div>
          
          {/* Warna Tahi */}
          <div className="mt-2">
            <label className="block text-xs text-yellow-700 mb-1">Warna Tahi</label>
            <div className="grid grid-cols-3 gap-2">
              {STOOL_COLORS.map(color => (
                <button
                  key={color.value}
                  type="button"
                  data-testid={`stool-color-${color.value.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  onClick={() => updateFormField('diaper_change.stool_color', 
                    formData.diaper_change?.stool_color === color.value ? '' : color.value)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all bg-white ${
                    formData.diaper_change?.stool_color === color.value
                      ? 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-300 scale-[1.03]'
                      : 'border-yellow-100 hover:border-yellow-300'
                  }`}
                >
                  <img src={color.img} alt={color.value} className="w-10 h-10 object-contain" />
                  <span className="text-[10px] font-medium text-gray-700 text-center leading-tight">{color.value}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Upload Gambar Najis */}
          <div className="mt-3">
            <label className="block text-xs text-yellow-700 mb-1">
              Upload gambar (optional)
            </label>
            {stoolPhotoFile || formData.diaper_change?.photo_url ? (
              <div className="relative inline-block">
                <img 
                  src={stoolPhotoFile ? URL.createObjectURL(stoolPhotoFile) : formData.diaper_change?.photo_url}
                  alt="Gambar najis"
                  className="w-20 h-20 object-cover rounded-lg border-2 border-yellow-300"
                />
                <button
                  type="button"
                  onClick={() => {
                    setStoolPhotoFile?.(null);
                    updateFormField('diaper_change.photo_url', '');
                  }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-dashed border-yellow-300 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors">
                <Upload className="w-4 h-4 text-yellow-600" />
                <span className="text-xs text-yellow-700">Pilih Gambar</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setStoolPhotoFile?.(file);
                  }}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>
      </div>

      {/* Gosok Gigi & Mandi */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-blue-800 mb-2">Gosok Gigi</label>
          <div className="flex flex-wrap gap-2">
            {['morning', 'afternoon', 'evening'].map(time => (
              <label key={time} className="flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg border cursor-pointer hover:bg-blue-50 min-w-[70px]">
                <input
                  type="checkbox"
                  checked={formData.brush_teeth?.[time] || false}
                  onChange={(e) => updateFormField(`brush_teeth.${time}`, e.target.checked)}
                  className="rounded w-4 h-4"
                />
                <span className="text-sm">{time === 'morning' ? 'Pagi' : time === 'afternoon' ? 'Petang' : 'Malam'}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-cyan-800 mb-2">Mandi</label>
          <div className="flex flex-wrap gap-2">
            {['morning', 'evening'].map(time => (
              <label key={time} className="flex items-center gap-1.5 bg-white px-3 py-2 rounded-lg border cursor-pointer hover:bg-cyan-50 min-w-[70px]">
                <input
                  type="checkbox"
                  checked={formData.bath?.[time] || false}
                  onChange={(e) => updateFormField(`bath.${time}`, e.target.checked)}
                  className="rounded w-4 h-4"
                />
                <span className="text-sm">{time === 'morning' ? 'Pagi' : 'Petang'}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Tidur */}
      <div>
        <label className="block text-sm font-medium text-indigo-800 mb-2">Tidur</label>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border">
              <span className="text-sm text-gray-600">Berapa kali:</span>
              <input
                data-testid="sleep-count-input"
                type="number"
                min="0"
                max="10"
                value={formData.sleep?.count || ''}
                onChange={(e) => updateFormField('sleep.count', e.target.value)}
                className="w-16 px-2 py-1 border border-indigo-200 rounded text-center text-sm"
                placeholder="0"
              />
            </div>
          </div>
          {(() => {
            const sleepCount = parseInt(formData.sleep?.count) || 0;
            const sessions = formData.sleep?.sessions || [];
            const updateSession = (idx, key, val) => {
              const next = [...sessions];
              next[idx] = { ...(next[idx] || {}), [key]: val };
              updateFormField('sleep.sessions', next);
              if (idx === 0) {
                updateFormField(key === 'start_time' ? 'sleep.start_time' : 'sleep.until_time', val);
              }
            };
            if (sleepCount >= 2) {
              return Array.from({ length: sleepCount }).map((_, idx) => (
                <div key={idx} className="bg-white px-3 py-2 rounded-lg border space-y-1" data-testid={`sleep-session-${idx + 1}`}>
                  <p className="text-xs font-semibold text-indigo-700">Kekerapan Tidur ({idx + 1})</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-600">Mula jam:</span>
                    <input
                      type="time"
                      value={sessions[idx]?.start_time || ''}
                      onChange={(e) => updateSession(idx, 'start_time', e.target.value)}
                      className="px-2 py-1 border border-indigo-200 rounded text-sm"
                    />
                    <span className="text-sm text-gray-600">Sehingga jam:</span>
                    <input
                      type="time"
                      value={sessions[idx]?.until_time || ''}
                      onChange={(e) => updateSession(idx, 'until_time', e.target.value)}
                      className="px-2 py-1 border border-indigo-200 rounded text-sm"
                    />
                  </div>
                </div>
              ));
            }
            return (
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border">
                  <span className="text-sm text-gray-600">Mula jam:</span>
                  <input
                    data-testid="sleep-start-time-input"
                    type="time"
                    value={formData.sleep?.start_time || ''}
                    onChange={(e) => updateSession(0, 'start_time', e.target.value)}
                    className="px-2 py-1 border border-indigo-200 rounded text-sm"
                  />
                </div>
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border">
                  <span className="text-sm text-gray-600">Sehingga jam:</span>
                  <input
                    data-testid="sleep-until-time-input"
                    type="time"
                    value={formData.sleep?.until_time || ''}
                    onChange={(e) => updateSession(0, 'until_time', e.target.value)}
                    className="px-2 py-1 border border-indigo-200 rounded text-sm"
                  />
                </div>
              </div>
            );
          })()}
          <input
            type="text"
            placeholder="Nota tidur..."
            value={formData.sleep?.notes || ''}
            onChange={(e) => updateFormField('sleep.notes', e.target.value)}
            className="w-full px-3 py-1 border border-indigo-200 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Mood */}
      <div>
        <label className="block text-sm font-medium text-purple-800 mb-2">Mood</label>
        <div className="flex gap-2">
          {[
            { value: 'sihat', emoji: '\u{1F970}', label: 'Sihat' },
            { value: 'sakit', emoji: '\u{1F912}', label: 'Sakit' },
            { value: 'mengantuk', emoji: '\u{1F634}', label: 'Mengantuk' },
            { value: 'sedih', emoji: '\u{1F622}', label: 'Sedih' }
          ].map(mood => (
            <button
              key={mood.value}
              type="button"
              onClick={() => updateFormField('mood', mood.value)}
              className={`text-2xl p-2 rounded-lg transition-all flex flex-col items-center ${
                formData.mood === mood.value ? 'bg-purple-200 ring-2 ring-purple-500' : 'hover:bg-gray-100'
              }`}
              title={mood.label}
            >
              {mood.emoji}
              <span className="text-xs mt-1">{mood.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nota Ibu Bapa</label>
        <textarea
          value={formData.notes || ''}
          onChange={(e) => updateFormField('notes', e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          placeholder="Sebarang catatan untuk ibu bapa..."
        />
      </div>

      {/* Infographic Modal */}
      {showInfographic && (
        <div
          data-testid="stool-infographic-modal"
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowInfographic(false)}
        >
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="absolute -top-12 right-0 flex items-center gap-2 z-10">
              <button
                type="button"
                data-testid="infographic-zoom-out"
                onClick={() => setInfographicZoom(z => Math.max(0.5, z - 0.25))}
                className="p-2 bg-white rounded-full shadow hover:bg-gray-100"
                title="Zoom keluar"
              >
                <ZoomOut className="w-5 h-5 text-gray-700" />
              </button>
              <span className="text-white text-sm font-medium bg-black/50 px-2 py-1 rounded">{Math.round(infographicZoom * 100)}%</span>
              <button
                type="button"
                data-testid="infographic-zoom-in"
                onClick={() => setInfographicZoom(z => Math.min(3, z + 0.25))}
                className="p-2 bg-white rounded-full shadow hover:bg-gray-100"
                title="Zoom masuk"
              >
                <ZoomIn className="w-5 h-5 text-gray-700" />
              </button>
              <button
                type="button"
                data-testid="infographic-close"
                onClick={() => setShowInfographic(false)}
                className="p-2 bg-red-500 rounded-full shadow hover:bg-red-600"
                title="Tutup"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="overflow-auto max-h-[80vh] rounded-xl bg-white">
              <img
                src="/assets/stool/infografik_najis.jpg"
                alt="Kenali Warna & Maksud Najis Bayi"
                className="w-full transition-transform origin-top-left"
                style={{ transform: `scale(${infographicZoom})`, transformOrigin: 'top left' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
